// LAP-XXX — Social Dashboard v2 : card "Relances suggérées".
//
// Affiche les contacts ≥ 4★ sans interaction depuis le seuil utilisateur
// (champ CustomUser.social_inactivity_threshold_days, défaut 30, borné 7..365).
// Chaque tuile = avatar + nom + rating + "X jours sans contact" + CTA
// (Appeler si Portable dispo, sinon Envoyer un message).
//
// Header : titre + bouton ⚙ ouvrant une modale pour modifier le seuil
// (PUT /api/Social/settings/).

import React, { useState } from 'react'
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined'
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined'
import CardHeader from '../../Investments/_shared/CardHeader'
import EmptyHelp from './EmptyHelp'
import { makePutRequest } from 'utils/api'
import {
  RELATIONSHIP_COLORS,
  StarsRow,
} from '../../MesContacts/_shared/RelationshipFields'

function getInitials(name) {
  if (!name) return '?'
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase()
}

function ThresholdDialog({ open, onClose, currentValue, onSaved }) {
  const { t } = useTranslation('layout')
  const [value, setValue] = useState(currentValue || 30)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  React.useEffect(() => {
    setValue(currentValue || 30)
    setError(null)
  }, [currentValue, open])

  const submit = async () => {
    const v = Number(value)
    if (!Number.isFinite(v) || v < 7 || v > 365) {
      setError(t('social.suggestions.thresholdHint', 'Entre 7 et 365 jours'))
      return
    }
    setSaving(true)
    try {
      const res = await makePutRequest(
        '/api/Social/settings/',
        { social_inactivity_threshold_days: v },
        { headers: { 'Content-Type': 'application/json' } },
        true,
      )
      if (res?.status === 200) {
        onSaved(v)
        onClose()
      } else {
        setError(res?.data?.detail || 'Error')
      }
    } catch (e) {
      setError(String(e?.message || e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('social.suggestions.thresholdLabel', "Seuil d'inactivité")}</DialogTitle>
      <DialogContent>
        <TextField
          type="number"
          fullWidth
          size="small"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          inputProps={{ min: 7, max: 365 }}
          helperText={
            error || t('social.suggestions.thresholdHint', 'Entre 7 et 365 jours')
          }
          error={Boolean(error)}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel', 'Annuler')}</Button>
        <Button variant="contained" onClick={submit} disabled={saving}>
          {t('common.save', 'Enregistrer')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function TaskRow({ task, onAction }) {
  const { t } = useTranslation('layout')
  const color = RELATIONSHIP_COLORS[task.relationship_type] || '#94a3b8'
  const daysBadge =
    task.days_since == null
      ? t('social.suggestions.neverContacted', 'Aucune interaction enregistrée')
      : t('social.suggestions.daysSince', {
          count: task.days_since,
          defaultValue_one: '{{count}} jour sans contact',
          defaultValue_other: '{{count}} jours sans contact',
          defaultValue: `${task.days_since} jours sans contact`,
        })
  const isCall = task.suggested_action === 'call'
  const ctaLabel = isCall
    ? t('social.suggestions.actionCall', 'Appeler')
    : t('social.suggestions.actionMessage', 'Envoyer un message')

  return (
    <Box
      display="flex"
      alignItems="center"
      sx={{
        p: 1,
        borderRadius: 1.5,
        backgroundColor: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.05)',
        '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
        transition: 'background 150ms ease',
        gap: 1.5,
      }}
    >
      <Avatar
        src={task.picture_link || undefined}
        sx={{
          width: 36,
          height: 36,
          backgroundColor: color,
          color: '#0e1320',
          fontSize: 13,
          fontWeight: 700,
        }}
      >
        {getInitials(task.contact_name)}
      </Avatar>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box
            sx={{
              fontWeight: 600,
              fontSize: 13.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {task.contact_name}
          </Box>
          <StarsRow value={task.rating} size={14} />
        </Box>
        <Box
          sx={{
            fontSize: 11,
            color: 'rgba(255,255,255,0.55)',
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
          }}
        >
          <Box
            component="span"
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: color,
              display: 'inline-block',
            }}
          />
          {daysBadge}
        </Box>
      </Box>
      <Button
        size="small"
        variant="contained"
        startIcon={isCall ? <PhoneOutlinedIcon /> : <ChatOutlinedIcon />}
        onClick={() => onAction(task)}
        sx={{
          textTransform: 'none',
          fontSize: 12,
          fontWeight: 600,
          backgroundColor: '#7c5cff',
          '&:hover': { backgroundColor: '#6a4be0' },
        }}
      >
        {ctaLabel}
      </Button>
    </Box>
  )
}

/**
 * @param {{
 *   data: Array,
 *   meta: { threshold_days: number },
 *   loading: boolean,
 *   onThresholdChange?: (n:number)=>void
 * }} props
 */
export default function SuggestionsCard({ data, meta, loading, onThresholdChange }) {
  const { t } = useTranslation('layout')
  const tasks = Array.isArray(data) ? data : []
  const threshold = meta?.threshold_days || 30
  const [openSettings, setOpenSettings] = useState(false)

  const onAction = (task) => {
    // Renvoie vers la fiche contact correspondante (le détail des CTA finaux
    // dépend de l'implémentation MyInbox/Tel ; ici on ouvre la fiche).
    if (task.contact_id) {
      window.location.assign(`/mes-contacts?cid=${task.contact_id}`)
    }
  }

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
        gap={1}
      >
        <CardHeader
          icon={<NotificationsActiveOutlinedIcon fontSize="small" />}
          title={t('social.suggestions.title', 'Relances suggérées')}
          subtitle={t('social.suggestions.subtitle', {
            count: threshold,
            defaultValue: `≥ ${threshold} jours sans contact`,
          })}
        />
        <IconButton
          size="small"
          onClick={() => setOpenSettings(true)}
          sx={{ color: 'rgba(255,255,255,0.65)' }}
          aria-label={t('social.suggestions.thresholdLabel', "Seuil d'inactivité")}
        >
          <SettingsOutlinedIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box mt={2} sx={{ overflowY: 'auto', flexGrow: 1 }}>
        {!loading && tasks.length === 0 ? (
          <EmptyHelp
            icon={<NotificationsActiveOutlinedIcon sx={{ fontSize: 22 }} />}
            title={t(
              'social.suggestions.empty',
              'Tous tes contacts importants sont à jour 🎉',
            )}
            description={t(
              'social.suggestions.emptyDescription',
              'Note tes contacts les plus importants (≥ 4 ⭐). Quand tu n’as pas interagi avec l’un d’eux depuis le seuil que tu as fixé, il apparaîtra ici avec une suggestion d’action.',
            )}
            actions={[
              {
                key: 'threshold',
                label: t('social.suggestions.thresholdLabel', "Seuil d'inactivité"),
                onClick: () => setOpenSettings(true),
              },
            ]}
          />
        ) : (
          <Stack spacing={0.75}>
            {tasks.map((task) => (
              <TaskRow
                key={`sg-${task.contact_id}`}
                task={task}
                onAction={onAction}
              />
            ))}
          </Stack>
        )}
      </Box>

      <ThresholdDialog
        open={openSettings}
        onClose={() => setOpenSettings(false)}
        currentValue={threshold}
        onSaved={(v) => onThresholdChange && onThresholdChange(v)}
      />
    </Box>
  )
}
